import { onMounted, onUnmounted, type Ref, ref, watch } from 'vue';
import type { AxiosResponse } from 'axios';
import type { ShowImageParams } from 'src/models/top';

type SavedImageEmitFn = {
  (event: 'component-resized'): void;
  (event: 'show-image', params: ShowImageParams): void;
};

interface SavedImageParam<T> {
  refMapElemInfoData: Ref<T>;
  emit: SavedImageEmitFn;
  fetchApiFn: () => Promise<AxiosResponse<Blob>>;
  // 画像取得をスキップする条件を持たないコンポーネントは渡さない設計のため optional
  skipFetchingImage?: Ref<boolean>;
  modalTitle?: Ref<string>;
  downloadFilenameTemplate: Ref<string>;
}

export default function useSavedImage<T>(
  params: SavedImageParam<T>,
): {
  savedImage: Ref<string | null>;
  showImage: () => void;
} {
  const savedImage = ref<string | null>(null);

  const fetchImageAsObjectURL = async(): Promise<string | null> => {
    try {
      const { data: blob } = await params.fetchApiFn();
      return URL.createObjectURL(blob);
    } catch (e) {
      return null;
    }
  };

  const updateSavedImage = async() => {
    if (savedImage.value) {
      URL.revokeObjectURL(savedImage.value);
      savedImage.value = null;
    }
    if (params.skipFetchingImage?.value) return;
    savedImage.value = await fetchImageAsObjectURL();
    params.emit('component-resized');
  };
  onMounted(updateSavedImage);
  onUnmounted(() => {
    if (savedImage.value) URL.revokeObjectURL(savedImage.value);
  });

  watch(params.refMapElemInfoData, updateSavedImage);

  const showImage = () => {
    const showImageParams: ShowImageParams = {
      modalTitle: params.modalTitle?.value || '',
      imageUrls: savedImage.value ? [savedImage.value] : [],
      downloadFilenameTemplate: params.downloadFilenameTemplate.value,
      initialIndex: 0,
    };
    params.emit('show-image', showImageParams);
  };

  return {
    savedImage,
    showImage,
  };
}
